import { useEffect, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, AlertTriangle, RefreshCw, CalendarClock, History, TrendingUp } from "lucide-react";

function mesActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtFecha(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
}

export default function Reports() {
  const { user } = useAuth();
  const puedeGenerar = user?.role === "admin" || user?.role === "jefe_servicio";
  const [sectores, setSectores] = useState([]);
  const [sectorId, setSectorId] = useState("__todos__");
  const [mes, setMes] = useState(mesActual());
  const [descargando, setDescargando] = useState(null);
  const [resumenSemanal, setResumenSemanal] = useState(null);
  const [generandoResumen, setGenerandoResumen] = useState(false);
  const [descargandoResumen, setDescargandoResumen] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [historialDesde, setHistorialDesde] = useState("");
  const [historialHasta, setHistorialHasta] = useState("");
  const [sectorComparativaId, setSectorComparativaId] = useState(null);
  const [comparativa, setComparativa] = useState(null);
  const [sectorComparativaIdB, setSectorComparativaIdB] = useState("__ninguno__");
  const [comparativaB, setComparativaB] = useState(null);

  useEffect(() => {
    api.get("/gis/sectores").then((r) => setSectores(r.data));
    loadResumenSemanal();
  }, []);

  const loadResumenSemanal = () =>
    api
      .get("/reports/resumen-semanal")
      .then((r) => {
        setResumenSemanal(r.data);
        const topSector = r.data?.sectores_criticos?.[0]?.sector_id;
        if (topSector) setSectorComparativaId(topSector);
      })
      .catch(() => {});

  useEffect(() => {
    if (sectorComparativaId) {
      api.get(`/reports/comparativa-sector/${sectorComparativaId}`).then((r) => setComparativa(r.data));
      if (sectorComparativaId === sectorComparativaIdB) setSectorComparativaIdB("__ninguno__");
    }
  }, [sectorComparativaId]);

  useEffect(() => {
    if (sectorComparativaIdB && sectorComparativaIdB !== "__ninguno__") {
      api.get(`/reports/comparativa-sector/${sectorComparativaIdB}`).then((r) => setComparativaB(r.data));
    } else {
      setComparativaB(null);
    }
  }, [sectorComparativaIdB]);

  const datosComparativa = comparativa
    ? comparativa.evolucion.map((row, i) => ({ ...row, indice_b: comparativaB?.evolucion?.[i]?.indice ?? null }))
    : [];

  const generarResumenAhora = async () => {
    setGenerandoResumen(true);
    try {
      const { data } = await api.post("/reports/resumen-semanal/generar-ahora");
      setResumenSemanal(data);
      if (data?.sectores_criticos?.[0]?.sector_id) setSectorComparativaId(data.sectores_criticos[0].sector_id);
      toast.success("Resumen semanal generado correctamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el resumen semanal");
    } finally {
      setGenerandoResumen(false);
    }
  };

  const descargarResumenPdf = async () => {
    setDescargandoResumen(true);
    try {
      const { data } = await api.get("/reports/resumen-semanal/exportar", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumen_semanal_${resumenSemanal?.periodo_inicio || mesActual()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Resumen semanal descargado (borrador — pendiente de revisión)");
    } catch (e) {
      toast.error("No se pudo descargar el resumen semanal");
    } finally {
      setDescargandoResumen(false);
    }
  };

  const toggleHistorial = () => {
    if (!mostrarHistorial && historial.length === 0) {
      api.get("/reports/resumen-semanal/historial").then((r) => setHistorial(r.data));
    }
    setMostrarHistorial((v) => !v);
  };

  const filtrarHistorial = () => {
    const params = new URLSearchParams();
    if (historialDesde) params.set("desde", historialDesde);
    if (historialHasta) params.set("hasta", historialHasta);
    api.get(`/reports/resumen-semanal/historial?${params.toString()}`).then((r) => setHistorial(r.data));
  };

  const limpiarFiltroHistorial = () => {
    setHistorialDesde("");
    setHistorialHasta("");
    api.get("/reports/resumen-semanal/historial").then((r) => setHistorial(r.data));
  };

  const descargar = async (formato) => {
    setDescargando(formato);
    try {
      const sector = sectorId === "__todos__" ? "" : sectorId;
      const { data } = await api.get(`/reports/mensual?mes=${mes}&sector_id=${sector}&formato=${formato}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe_${mes}.${formato === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Informe descargado (borrador — pendiente de revisión)");
    } catch (e) {
      toast.error("No se pudo generar el informe");
    } finally {
      setDescargando(null);
    }
  };

  return (
    <AppLayout title="Informes mensuales por sector">
      <Card className="max-w-2xl mb-6" data-testid="weekly-summary-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm uppercase tracking-widest flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-valoriza-leaf" /> Resumen semanal
          </CardTitle>
          {puedeGenerar && (
            <Button onClick={generarResumenAhora} disabled={generandoResumen} size="sm" variant="outline" data-testid="generate-weekly-summary-button" className="gap-2">
              <RefreshCw className={`h-3.5 w-3.5 ${generandoResumen ? "animate-spin" : ""}`} /> {generandoResumen ? "Generando..." : "Generar ahora"}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!resumenSemanal && (
            <p className="text-sm text-muted-foreground" data-testid="weekly-summary-empty">
              Todavía no se ha generado ningún resumen semanal. Se genera automáticamente cada lunes a las 06:00 con los
              sectores más críticos de la semana anterior.
            </p>
          )}
          {resumenSemanal && (
            <div className="space-y-3" data-testid="weekly-summary-content">
              <p className="text-xs text-muted-foreground">
                Semana del {fmtFecha(resumenSemanal.periodo_inicio)} al {fmtFecha(resumenSemanal.periodo_fin)} · {resumenSemanal.total_fichas_iris_semana} quejas IRIS ·{" "}
                {resumenSemanal.total_incidencias_semana} incidencias nuevas
              </p>
              {resumenSemanal.sectores_criticos.length === 0 && (
                <p className="text-sm text-muted-foreground">Sin actividad relevante registrada esa semana.</p>
              )}
              {resumenSemanal.sectores_criticos.map((s, i) => (
                <div key={s.sector_id} className="flex items-center justify-between border-b border-border pb-2 last:border-0" data-testid={`weekly-summary-sector-${i}`}>
                  <div>
                    <p className="text-sm font-bold">{s.nombre}</p>
                    <p className="text-xs text-muted-foreground">
                      {s.quejas_iris} quejas IRIS · {s.incidencias_nuevas} incidencias nuevas
                    </p>
                  </div>
                  <Badge variant={i === 0 ? "destructive" : "outline"}>{s.indice}</Badge>
                </div>
              ))}
              {resumenSemanal.zonas_baja_cobertura?.length > 0 && (
                <div className="pt-3 border-t border-border" data-testid="weekly-summary-coverage-zones">
                  <p className="text-xs uppercase tracking-widest font-bold text-accent-alert mb-2">Zonas de baja cobertura GPS más repetidas</p>
                  {resumenSemanal.zonas_baja_cobertura.map((z, i) => (
                    <div key={`${z.lat}-${z.lon}`} className="flex items-center justify-between text-sm py-1" data-testid={`weekly-summary-coverage-zone-${i}`}>
                      <span className="text-muted-foreground">{z.lat}, {z.lon}</span>
                      <Badge variant="outline">{z.veces}x</Badge>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 pt-2">
                <Button onClick={descargarResumenPdf} disabled={descargandoResumen} size="sm" variant="outline" data-testid="download-weekly-summary-pdf-button" className="gap-2">
                  <FileText className="h-3.5 w-3.5" /> {descargandoResumen ? "Generando..." : "Descargar PDF"}
                </Button>
                <button onClick={toggleHistorial} data-testid="toggle-weekly-summary-history-button" className="text-xs underline text-valoriza-leaf font-bold flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5" /> {mostrarHistorial ? "Ocultar historial" : "Ver historial"}
                </button>
              </div>
              {mostrarHistorial && (
                <div className="pt-2 border-t border-border space-y-3" data-testid="weekly-summary-history-list">
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Desde</Label>
                      <Input type="date" value={historialDesde} onChange={(e) => setHistorialDesde(e.target.value)} className="h-8 text-xs w-36" data-testid="history-filter-desde-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Hasta</Label>
                      <Input type="date" value={historialHasta} onChange={(e) => setHistorialHasta(e.target.value)} className="h-8 text-xs w-36" data-testid="history-filter-hasta-input" />
                    </div>
                    <Button onClick={filtrarHistorial} size="sm" variant="outline" data-testid="history-filter-apply-button" className="h-8 text-xs">
                      Filtrar
                    </Button>
                    {(historialDesde || historialHasta) && (
                      <button onClick={limpiarFiltroHistorial} data-testid="history-filter-clear-button" className="text-xs underline text-muted-foreground">
                        Limpiar
                      </button>
                    )}
                  </div>
                  {historial.length === 0 && <p className="text-xs text-muted-foreground">Sin resúmenes en ese rango de fechas.</p>}
                  {historial.map((h) => (
                    <div key={h._id} className="flex items-center justify-between text-xs" data-testid={`weekly-summary-history-item-${h._id}`}>
                      <span className="text-muted-foreground">
                        {fmtFecha(h.periodo_inicio)}–{fmtFecha(h.periodo_fin)}
                      </span>
                      <span>
                        {h.sectores_criticos?.[0]
                          ? `${h.sectores_criticos[0].nombre} (${h.sectores_criticos[0].indice})`
                          : "Sin actividad"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="max-w-2xl mb-6" data-testid="sector-comparativa-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm uppercase tracking-widest flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-valoriza-leaf" /> Comparativa semanal del sector crítico
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={sectorComparativaId || ""} onValueChange={setSectorComparativaId}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="comparativa-sector-select">
                <SelectValue placeholder="Elegir sector" />
              </SelectTrigger>
              <SelectContent>
                {sectores.map((s) => (
                  <SelectItem key={s._id} value={s._id}>
                    {s.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sectorComparativaIdB} onValueChange={setSectorComparativaIdB}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="comparativa-sector-select-b">
                <SelectValue placeholder="Comparar con..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ninguno__">Comparar con...</SelectItem>
                {sectores.filter((s) => s._id !== sectorComparativaId).map((s) => (
                  <SelectItem key={s._id} value={s._id}>
                    {s.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {(!comparativa || comparativa.evolucion.length < 2) && (
            <p className="text-sm text-muted-foreground" data-testid="sector-comparativa-empty">
              Todavía no hay suficientes resúmenes semanales acumulados (se necesitan al menos 2) para trazar la
              evolución de este sector.
            </p>
          )}
          {comparativa && comparativa.evolucion.length >= 2 && (
            <ResponsiveContainer width="100%" height={240} data-testid="sector-comparativa-chart">
              <LineChart data={datosComparativa}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="periodo_inicio" tickFormatter={fmtFecha} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={fmtFecha} />
                <Line type="monotone" dataKey="quejas_iris" name="Quejas IRIS" stroke="#0A84FF" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="incidencias_nuevas" name="Incidencias nuevas" stroke="#FF9F0A" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="indice" name={`Índice — ${comparativa.nombre}`} stroke="#FF3B30" strokeWidth={2} dot={{ r: 3 }} />
                {comparativaB && (
                  <Line
                    type="monotone"
                    dataKey="indice_b"
                    name={`Índice — ${comparativaB.nombre}`}
                    stroke="#7AB800"
                    strokeWidth={2}
                    strokeDasharray="5 3"
                    dot={{ r: 3 }}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-widest">Generar informe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-2 rounded-md border border-accent-warning/40 bg-accent-warning/5 px-3 py-2 text-xs">
            <AlertTriangle className="h-4 w-4 text-accent-warning shrink-0" />
            Todo informe se marca como "borrador — pendiente de revisión" hasta su validación humana antes de enviarlo al Ajuntament o a terceros.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Sector</Label>
              <Select value={sectorId} onValueChange={setSectorId}>
                <SelectTrigger data-testid="report-sector-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todos__">Todos los sectores</SelectItem>
                  {sectores.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Mes</Label>
              <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} data-testid="report-month-input" />
            </div>
          </div>

          <div className="flex gap-3">
            <Button data-testid="download-pdf-button" disabled={descargando} onClick={() => descargar("pdf")} className="gap-2 bg-valoriza-dark text-white hover:bg-valoriza-dark/90">
              <FileText className="h-4 w-4" /> {descargando === "pdf" ? "Generando..." : "Descargar PDF"}
            </Button>
            <Button data-testid="download-xlsx-button" disabled={descargando} onClick={() => descargar("xlsx")} variant="outline" className="gap-2">
              <FileSpreadsheet className="h-4 w-4" /> {descargando === "xlsx" ? "Generando..." : "Descargar Excel"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </AppLayout>
  );
}
